import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom-v5-compat';

import { FormOnSubmitType, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { useGetSsoAuthUrl } from 'utils/customHooks/api/useGetSsoAuthUrl';
import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { useSelectorJs } from 'utils/customHooks';
import { selectFeatureFlag } from 'global/openpathconfig/selectors';
import { getWindowLocation } from 'utils/window';

import { useIsSso } from '../../hooks/useIsSso';
import { LoginSSOEmailForm } from '../LoginSSOEmailForm/LoginSSOEmailForm';

import { avigilonAltaIdTooltipText } from './constants';

import './LoginSSOAltaIdForm.scss';

interface LoginSSOAltaIdFormData {
  uuid: string;
}

interface LoginSSOAltaIdFormProps {
  loginRoute: '/login' | '/signin';
  redirectUrl?: string;
}

/**
 * @component
 * @description Component used to login w/ SSO via an Avigilon Alta id. This id is a uuid associated with their IdP
 * which for now should be the samlJit IdP associated with the SAML SSO App. The uuid is used to find the login url
 * of the associated IdP and should error if non returned (the mutation would catch this)
 */
export const LoginSSOAltaIdForm = ({
  loginRoute,
  redirectUrl,
}: LoginSSOAltaIdFormProps) => {
  const getSsoAuthUrlMutation = useGetSsoAuthUrl();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const enabledFeatureCodes = useSelectorJs(
    selectFeatureFlag('ENABLED_FEATURE_CODES'),
  );

  const isSso = useIsSso();

  // Hide behind feature flag for now
  if (
    !enabledFeatureCodes.includes('samlSso') ||
    !enabledFeatureCodes.includes('samlJit')
  ) {
    // If for some reason this component rendered return the default email version until release
    return <LoginSSOEmailForm loginRoute={loginRoute} />;
  }

  const onSubmit: FormOnSubmitType<LoginSSOAltaIdFormData> = async ({
    values: { uuid },
  }) => {
    const res = await getSsoAuthUrlMutation.mutateAsync({
      payload: {
        uuid,
        ...(redirectUrl && { redirectUrl }),
      },
    });

    if (res?.authUrl) {
      getWindowLocation().href = res.authUrl;
    }
  };

  return (
    <OpForm
      // No GET call so no error
      className={isSso ? '' : 'email-form'}
      hasError={false}
      onSubmit={onSubmit}
      initialValues={{ uuid: '' }}
      defaultButtons={false}
    >
      <OpForm.Input
        name="uuid"
        label={t('Avigilon Alta Id')}
        required
        tooltip={t(avigilonAltaIdTooltipText)}
        rules={[
          { required: true, message: t('Please enter your Avigilon Alta Id') },
        ]}
        testId="avigilon-alta-id-input"
      />

      <OpForm.Button
        block
        className="login-sso-email-form__continue-button"
        type="primary"
        htmlType="submit"
        loading={getSsoAuthUrlMutation.isPending}
      >
        {t('Continue')}
      </OpForm.Button>

      <OpDivider plain>or</OpDivider>

      <OpForm.Button
        block
        onClick={() => {
          // Using navigate here as we don't have access to
          // changeRoute yet since it is in Auth container
          navigate(loginRoute);
        }}
      >
        {isSso ? t('Sign in without SSO') : t('Sign in without Alta Id')}
      </OpForm.Button>
    </OpForm>
  );
};
